import React, { useState, useEffect } from 'react';
import { Box, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { useGetPresentsBenefitsPageQuery } from '../../redux/api/content.api';
import Loader from '../../components/common/loader';
import Page404 from '../page404';
import ContentSection from '../../components/common/ContentSection';
import BenefitsGrid from '../../components/common/BenefitsGrid';
import Benefit from '../shared/benefit/benefit';
import ReactHtmlParser from "react-html-parser";
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { addPopup } from '../../redux/actions';
import * as popups from '../../constants/popup-types';
import SliderContent from '../new_home/components/slider-content';
import '../new_home/components/slider-content/index.scss';

const SingleBenefitContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: '20px',
  padding: '20px 0',
  width: '100%',
  '& > *': {
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .benefit-wrapper': {
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '18px',
  },
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    padding: '16px 0',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  '&.single-benefit': {
    gridTemplateColumns: '1fr',
    justifyContent: 'center',
    maxWidth: 'calc((100% - 80px) / 5)',
    margin: '0 auto',
    '& > *': {
      maxWidth: '100%',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      maxWidth: 'calc((100% - 54px) / 4)',
    },
    '@media (max-width: 1200px)': {
      maxWidth: 'calc((100% - 32px) / 3)',
    },
  },
}));

const BenefitsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0',
  paddingBottom: '30px',
}));

const CenteredSliderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  '& .homepage-slider-content': {
    maxWidth: 'fit-content',
  }
}));

const PresentsBenefitsPage = () => {
  const params = useParams();
  const text = useSelector((store) => store.config.Translations);
  const [sections, setSections] = useState([]);
  
  const [presentBenefits, setPresentBenefits] = useState([]);
  
  const [relatedBenefits, setRelatedBenefits] = useState([]);

  const [isValid, setIsValid] = useState(false);
  const { data, isLoading, isError } = useGetPresentsBenefitsPageQuery({
    name: params.name
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (data) {
      setSections(data.contentSections || []);
      setPresentBenefits(data.presentBenefits || []);
      setRelatedBenefits(data.relatedBenefits || []);
      setIsValid(data.isValid || false);
    }
  }, [data]);

  if (isLoading) return <Loader className="initial-loader" />;
  if (isError) return <Page404 />;

  const errorMessage = data?.errorMessage;
  return (
    <div style={{
      direction: 'rtl',
    }}>
    <Box sx={{ py: 4 }}>
        {sections[0] && (
              <ContentSection key={sections[0].id} section={sections[0]} />
        )}
        {(!isValid && errorMessage) ? (
            <Container style={{display: 'flex', flexDirection: 'column', textAlign: 'center', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', marginTop: '0px'}}>
                <h1>{ReactHtmlParser(errorMessage)}</h1>
                {data?.selectedBenefit && (
                    <BenefitsContainer>
                      {data.selectedBenefit.length === 1 && !isMobile ? (
                        <SingleBenefitContainer className="single-benefit">
                          <Benefit key={data.selectedBenefit[0].id} data={data.selectedBenefit[0]} />
                        </SingleBenefitContainer>
                        ) : data.selectedBenefit.length === 1 && isMobile ?  (
                         <Box sx={{
                           display: 'flex', 
                           justifyContent: 'center', 
                           width: '100%',
                           '& > *': {
                             maxWidth: 'calc(320px / 1.5)',
                             width: '100%',
                           }
                         }}>
                              <Benefit key={data.selectedBenefit[0].id} data={data.selectedBenefit[0]} />
                          </Box>
                      ) : data.selectedBenefit.length > 1 (
                        <SingleBenefitContainer className="single-benefit">
                          <BenefitsGrid
                            benefits={data?.selectedBenefit?.map(benefit => 
                              <Benefit key={benefit.id} data={benefit} />
                            )}
                          />
                        </SingleBenefitContainer>
                      )}
                    </BenefitsContainer>
                )}
            </Container>
        ) : null}

        <>
          
          {presentBenefits.length > 0 && isValid && (
            <BenefitsContainer>
              {presentBenefits.length === 1 ? (
                <SingleBenefitContainer className="single-benefit">
                  <Benefit key={presentBenefits[0].id} data={presentBenefits[0]} />
                </SingleBenefitContainer>
              ) : (
                <BenefitsGrid 
                  benefits={presentBenefits.map((benefit) => (
                    <Benefit key={benefit.id} data={benefit} />
                  ))}
                >
                  {presentBenefits.map((benefit) => (
                    <Benefit key={benefit.id} data={benefit} />
                  ))}
                </BenefitsGrid>
              )}
            </BenefitsContainer>
          )}
          {sections[1] && (
            <ContentSection key={sections[1].id} section={sections[1]} />
          )}

          {relatedBenefits.length > 0 && (
            <BenefitsContainer>
              {isMobile && relatedBenefits.length > 1 ? (
                <SliderContent
                  itemsList={relatedBenefits.map((benefit) => (
                    <Benefit key={benefit.id} data={benefit} />
                  ))}
                />
              ) : relatedBenefits.length === 1 ? (
                <SingleBenefitContainer className="single-benefit">
                  <Benefit key={relatedBenefits[0].id} data={relatedBenefits[0]} />
                </SingleBenefitContainer>
              ) : (
                <BenefitsGrid 
                  benefits={relatedBenefits.map((benefit) => (
                    <Benefit key={benefit.id} data={benefit} />
                  ))}
                >
                  {relatedBenefits.map((benefit) => (
                    <Benefit key={benefit.id} data={benefit} />
                  ))}
                </BenefitsGrid>
              )}
            </BenefitsContainer>
          )}

          {/* Fallback: Show any benefits that might not be categorized */}
          {data?.selectedBenefit && data.selectedBenefit.length > 0 && !presentBenefits.length && !relatedBenefits.length && (
            <BenefitsContainer>
              {data.selectedBenefit.length === 1 ? (
                <SingleBenefitContainer className="single-benefit">
                  <Benefit key={data.selectedBenefit[0].id} data={data.selectedBenefit[0]} />
                </SingleBenefitContainer>
              ) : (
                <BenefitsGrid
                  benefits={data.selectedBenefit.map(benefit => 
                    <Benefit key={benefit.id} data={benefit} />
                  )}
                />
              )}
            </BenefitsContainer>
          )}  
          
          {sections[2] && (
            <ContentSection key={sections[2].id} section={sections[2]} />
          )}
        </>
    </Box>
    </div>
  );
};

export default PresentsBenefitsPage;
